import * as React from 'react';
import {
    Col,
    Form,
    InputNumber,
    Spin,
    message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import DockVehicleMapping from 'components/address/DockVehicleMapping';
import { getVehicleMakeList } from 'network/common.api';
import { dockConfigurationFieldStyles as styles } from './dock-configuration-field.styles';

interface DockConfigurationFieldProps {
    form: any;
    params: any;
    parentObj: string;
}

const DockConfigurationField: React.FC<DockConfigurationFieldProps> = ({
    form,
    params,
    parentObj,
}) => {
    const { t } = useTranslation();
    const [vehicleMakeOptions, setVehicleMakeOptions] = React.useState<Array<{ value: string; label: string }>
    >([]);
    const [isVehicleMakeLoading, setIsVehicleMakeLoading] = React.useState<boolean>(false);

    const isDisabled = !!params?.disabledFields?.[parentObj];
    const dockCapacity = Form.useWatch([parentObj, 'dockCapacity'], form);

    React.useEffect(() => {
        let isMounted = true;
        const fetchVehicleMakes = async () => {
            setIsVehicleMakeLoading(true);
            const response = await getVehicleMakeList();
            if (!isMounted) return;
            if (response.isSuccess) {
                const list = Array.isArray(response.data) ? response.data : [];
                setVehicleMakeOptions(
                    list
                        .map((vehicleMake: { name?: string; code?: string }) => {
                            let label = '';
                            if (vehicleMake.name) {
                                label = vehicleMake.code
                                    ? `${vehicleMake.name} (${vehicleMake.code})`
                                    : vehicleMake.name;
                            } else {
                                label = vehicleMake.code || '';
                            }
                            return {
                                value: vehicleMake.code || vehicleMake.name || '',
                                label,
                            };
                        })
                        .filter((option: { value: string; label: string }) => option.value),
                );
            } else if (response.errorMessage) {
                message.error(response.errorMessage);
            }
            setIsVehicleMakeLoading(false);
        };
        fetchVehicleMakes();
        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Col span={24}>
            <div style={styles.wrapper}>
                {/* Grace Time + Dock Capacity side-by-side, mirroring addressRow in create-address */}
                <div style={styles.row}>
                    <div style={styles.item}>
                        <span style={styles.label}>{`${t('graceTime')} (sec)`}</span>
                        <Form.Item
                            name={[parentObj, 'graceTime']}
                            style={{ marginBottom: 0 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder={t('enterSecondsGreaterThanZero')}
                                min={1}
                                precision={0}
                                disabled={isDisabled}
                            />
                        </Form.Item>
                    </div>
                    <div style={styles.item}>
                        <span style={styles.label}>{t('dockCapacity')}</span>
                        <Form.Item
                            name={[parentObj, 'dockCapacity']}
                            style={{ marginBottom: 0 }}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder={t('dockCapacity')}
                                min={1}
                                precision={0}
                                disabled={isDisabled}
                            />
                        </Form.Item>
                    </div>
                </div>

                {/* Dock Vehicle Mapping — full width, below */}
                <div style={styles.dockMappingWrapper}>
                    <span style={styles.label}>{t('dockToVehicleMapping')}</span>
                    {isVehicleMakeLoading ? (
                        <Spin />
                    ) : (
                        <Form.Item
                            name={[parentObj, 'dockVehicleMapping']}
                            style={{ marginBottom: 0 }}
                        >
                            <DockVehicleMapping
                                dockCapacity={Number(dockCapacity) || 0}
                                vehicleMakeOptions={vehicleMakeOptions}
                                disabled={isDisabled}
                            />
                        </Form.Item>
                    )}
                </div>
            </div>
        </Col>
    );
};

export default DockConfigurationField;
